import { useEffect, useRef, useState } from 'react'
import { cn } from '@/ui/lib/utils'
import { CHART_FONT_FAMILY } from '@/ui/lib/quantChart/format'
import type { EquityChartHandle } from '@/ui/lib/quantChart/equityChart'

export interface QuantEquityChartProps {
  /** `[UTC epoch seconds, equity]`, as the server stores them. */
  points: ReadonlyArray<readonly [number, number]>
  className?: string
  /** The dim label in the top-left corner; defaults to the stored-result wording of a run. */
  label?: string
}

const DIM_TEXT = 'rgba(244,241,234,0.42)'

/**
 * Equity-only chart (spec 038 20.6 rule 4): one pane, the accent step line,
 * UTC time axis, wheel zoom and crosshair from the shared options, fitted to
 * all points. No candles, scrubber, Bars select or readout. The library is
 * loaded by a dynamic import on mount (lazy, like `QuantChart`), and the
 * chart fills its container.
 */
export function QuantEquityChart({ points, className, label = 'EQUITY · STORED RESULT' }: QuantEquityChartProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<EquityChartHandle | null>(null)
  const latest = useRef(points)
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    latest.current = points
    handleRef.current?.setData(points)
  }, [points])

  useEffect(() => {
    const root = rootRef.current
    const host = hostRef.current
    if (!root || !host) return
    let cancelled = false
    import('@/ui/lib/quantChart/equityChart')
      .then((m) => {
        if (cancelled) return
        handleRef.current = m.createEquityChart(host, root, latest.current)
      })
      .catch((error: unknown) => {
        console.error('Equity chart failed to load', error)
        if (!cancelled) setUnavailable(true)
      })
    return () => {
      cancelled = true
      handleRef.current?.dispose()
      handleRef.current = null
    }
  }, [])

  return (
    <div
      ref={rootRef}
      role="img"
      aria-label="Equity curve"
      data-quant-equity-root=""
      className={cn('relative h-full w-full cursor-grab overflow-hidden bg-ink active:cursor-grabbing', className)}
    >
      <div ref={hostRef} className="absolute inset-0" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-[14px] left-[10px] z-10 -translate-y-1/2 text-[10px] leading-none whitespace-nowrap select-none"
        style={{ fontFamily: CHART_FONT_FAMILY, color: DIM_TEXT }}
      >
        {label}
      </div>
      {unavailable ? (
        <div role="status" className="absolute inset-0 z-20 flex items-center justify-center font-mono text-[12px] text-paper-50">
          Chart unavailable
        </div>
      ) : null}
    </div>
  )
}
